import React from 'react';
import { 
  Box, 
  Typography, 
  Paper,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  Chip,
  Divider
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ReceiptIcon from '@mui/icons-material/Receipt';
import TuneIcon from '@mui/icons-material/Tune';

const formatOptions = [
  {
    id: 'csv',
    label: 'CSV File',
    icon: DescriptionIcon,
    description: 'Spreadsheet format for Excel or Google Sheets',
    color: '#22c55e'
  },
  {
    id: 'pdf',
    label: 'PDF Report',
    icon: PictureAsPdfIcon,
    description: 'Summary report with totals and breakdowns',
    color: '#8b5cf6'
  },
  {
    id: 'pdf_images',
    label: 'PDF with Images',
    icon: ImageIcon,
    description: 'Full report with receipt images attached',
    color: '#3b82f6',
    isRecommended: true
  }
];

const dataLevelOptions = [
  { id: 'receipt', label: 'Receipt Level', description: 'One row per receipt with totals' },
  { id: 'item', label: 'Line Item Level', description: 'One row per item with full details' }
];

const imageLayoutOptions = [
  { id: 'one_per_page', label: 'One receipt per page', description: 'Best for printing or archiving' },
  { id: 'compact', label: 'Compact layout', description: 'Data summary with thumbnail images' },
  { id: 'appendix', label: 'Images appendix', description: 'Data first, all images at the end' }
];

const imageQualityOptions = [
  { id: 'original', label: 'Original quality', description: 'Larger file size' },
  { id: 'optimized', label: 'Optimized', description: '~70% smaller', isRecommended: true },
  { id: 'thumbnail', label: 'Thumbnails only', description: 'Smallest file size' }
];

// Reusable section container
const OptionsSection = ({ icon: Icon, title, color, children }) => (
  <Box sx={{ 
    mt: 3, 
    p: 2.5, 
    borderRadius: 2, 
    backgroundColor: `${color}08`,
    border: `1px solid ${color}25`
  }}>
    <Typography variant="subtitle2" sx={{ 
      mb: 2, 
      fontWeight: 600, 
      color: color, 
      display: 'flex', 
      alignItems: 'center', 
      gap: 1 
    }}>
      <Icon sx={{ fontSize: 18 }} />
      {title}
    </Typography>
    {children}
  </Box>
);

// Reusable radio option group
const RadioOptionGroup = ({ options, value, onChange, columns = 1, compact = false }) => (
  <FormControl component="fieldset" fullWidth>
    <RadioGroup value={value} onChange={(e) => onChange(e.target.value)}>
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: columns > 1 ? { xs: '1fr', sm: `repeat(${columns}, 1fr)` } : '1fr',
        gap: 1 
      }}>
        {options.map((option) => (
          <Paper
            key={option.id}
            onClick={() => onChange(option.id)}
            elevation={0}
            sx={{
              px: 1.5,
              py: compact ? 1 : 1.25,
              cursor: 'pointer',
              border: value === option.id 
                ? '1px solid rgba(59, 130, 246, 0.5)' 
                : '1px solid rgba(255, 255, 255, 0.08)',
              backgroundColor: value === option.id 
                ? 'rgba(59, 130, 246, 0.1)' 
                : 'transparent',
              borderRadius: 1,
              transition: 'all 0.15s ease',
              '&:hover': {
                borderColor: 'rgba(59, 130, 246, 0.3)',
                backgroundColor: 'rgba(59, 130, 246, 0.05)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Radio 
                value={option.id} 
                size="small" 
                sx={{ p: 0 }}
                checked={value === option.id}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                    {option.label}
                  </Typography>
                  {option.isRecommended && (
                    <Chip 
                      label="Recommended" 
                      size="small" 
                      sx={{ 
                        height: 16, 
                        fontSize: '0.6rem', 
                        fontWeight: 600,
                        backgroundColor: 'rgba(59, 130, 246, 0.2)',
                        color: '#60a5fa',
                        '& .MuiChip-label': { px: 0.75 }
                      }} 
                    />
                  )}
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {option.description}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
    </RadioGroup>
  </FormControl>
);

function ExportOptionsPanel({ 
  exportFormat, 
  setExportFormat,
  imageLayout,
  setImageLayout,
  imageQuality,
  setImageQuality,
  includeLineItems,
  setIncludeLineItems,
  includePageNumbers,
  setIncludePageNumbers,
  includeImageAppendix,
  setIncludeImageAppendix,
  // Props for data level (individual mode)
  showExportTypeOption = false,
  exportType,
  setExportType
}) {
  const showImageOptions = exportFormat === 'pdf_images';
  const showDataLevelOptions = showExportTypeOption && (exportFormat === 'csv' || exportFormat === 'pdf');

  return (
    <Box>
      {/* Format Selection */}
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
        Export Format
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {formatOptions.map((format) => {
          const IconComponent = format.icon;
          const isSelected = exportFormat === format.id;
          
          return (
            <Paper
              key={format.id}
              onClick={() => setExportFormat(format.id)}
              sx={{
                flex: '1 1 140px',
                minWidth: 140,
                p: 2,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                border: isSelected 
                  ? `2px solid ${format.color}` 
                  : '2px solid rgba(255, 255, 255, 0.1)',
                backgroundColor: isSelected 
                  ? `${format.color}15` 
                  : 'rgba(255, 255, 255, 0.02)',
                borderRadius: 2,
                position: 'relative',
                '&:hover': {
                  borderColor: format.color,
                  backgroundColor: `${format.color}10`
                }
              }}
            >
              {format.isRecommended && (
                <Chip
                  label="Recommended"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: -10,
                    right: 8,
                    height: 20,
                    fontSize: '0.6rem',
                    fontWeight: 600,
                    backgroundColor: format.color,
                    color: 'white'
                  }}
                />
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <IconComponent sx={{ color: format.color, fontSize: 22 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                  {format.label}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.3 }}>
                {format.description}
              </Typography>
              
              {isSelected && (
                <Box sx={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: format.color
                }} />
              )}
            </Paper>
          );
        })}
      </Box>

      {/* Data Level Options - Show for CSV/PDF in individual mode */}
      {showDataLevelOptions && (
        <OptionsSection icon={TableRowsIcon} title="Data Level" color="#8b5cf6">
          <RadioOptionGroup
            options={dataLevelOptions}
            value={exportType}
            onChange={setExportType}
            columns={2}
          />
        </OptionsSection>
      )}

      {/* Image Options - Show for PDF with Images */}
      {showImageOptions && (
        <OptionsSection icon={ImageIcon} title="Image Options" color="#3b82f6">
          {/* Layout */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ 
              fontWeight: 600, 
              color: 'text.secondary', 
              mb: 1, 
              display: 'block',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontSize: '0.65rem'
            }}>
              Layout
            </Typography>
            <RadioOptionGroup
              options={imageLayoutOptions}
              value={imageLayout}
              onChange={setImageLayout}
            />
          </Box>

          <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.06)' }} />

          {/* Quality */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ 
              fontWeight: 600, 
              color: 'text.secondary', 
              mb: 1, 
              display: 'block',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontSize: '0.65rem'
            }}>
              Image Quality
            </Typography>
            <RadioOptionGroup
              options={imageQualityOptions}
              value={imageQuality}
              onChange={setImageQuality}
              columns={3}
              compact
            />
          </Box>

          <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.06)' }} />

          {/* Additional Options */}
          <Box>
            <Typography variant="caption" sx={{ 
              fontWeight: 600, 
              color: 'text.secondary', 
              mb: 0.5, 
              display: 'block',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontSize: '0.65rem'
            }}>
              Additional Options
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={includeLineItems}
                    onChange={(e) => setIncludeLineItems(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Include line item tables</Typography>}
                sx={{ m: 0 }}
              />
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={includePageNumbers}
                    onChange={(e) => setIncludePageNumbers(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Add page numbers</Typography>}
                sx={{ m: 0 }}
              />
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={includeImageAppendix}
                    onChange={(e) => setIncludeImageAppendix(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', textAlign: 'left' }}>
                    Include full-size image appendix
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', textAlign: 'left' }}>
                      Adds all receipt images at full size at the end of the PDF
                    </Typography>
                  </Typography>
                }
                sx={{ m: 0 }}
              />
            </Box>
          </Box>
        </OptionsSection>
      )}

      {/* Info boxes for formats without extra options */}
      {exportFormat === 'csv' && !showDataLevelOptions && (
        <Box sx={{ 
          mt: 3, 
          p: 2, 
          borderRadius: 1.5, 
          backgroundColor: 'rgba(34, 197, 94, 0.08)',
          border: '1px solid rgba(34, 197, 94, 0.2)'
        }}>
          <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
            <strong style={{ color: '#22c55e' }}>CSV Export:</strong> Creates a spreadsheet file with all receipt data. 
            Perfect for importing into Excel, Google Sheets, or accounting software.
          </Typography>
        </Box>
      )}

      {exportFormat === 'pdf' && !showDataLevelOptions && (
        <Box sx={{ 
          mt: 3, 
          p: 2, 
          borderRadius: 1.5, 
          backgroundColor: 'rgba(139, 92, 246, 0.08)',
          border: '1px solid rgba(139, 92, 246, 0.2)'
        }}>
          <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
            <strong style={{ color: '#8b5cf6' }}>PDF Report:</strong> Generates a formatted summary report with 
            totals, category breakdowns, and merchant analysis.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default ExportOptionsPanel;
